import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import useSchool, { BRAND_COLOR_FALLBACK } from '@/hooks/useSchool';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Brain, Hourglass, Loader2, Sparkles, Trophy } from 'lucide-react';

function reasonScoreClasses(score) {
  if (score == null) return 'bg-slate-100 text-slate-500 border-slate-200';
  if (score >= 4) return 'bg-emerald-50 text-emerald-700 border-emerald-300';
  if (score >= 2) return 'bg-amber-50 text-amber-700 border-amber-300';
  return 'bg-rose-50 text-rose-700 border-rose-300';
}

export default function StudentAssessmentResult() {
  const navigate = useNavigate();
  const { tid } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [child, setChild] = useState(null);
  const [missions, setMissions] = useState([]);
  const school = useSchool(child?.school_id);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/me/attempts/${tid}`);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load result');
    } finally { setLoading(false); }
  }, [tid]);

  const loadMissions = useCallback(async () => {
    try {
      const { data: md } = await api.get('/me/missions');
      // Filter to missions sourced from THIS attempt
      const myMissions = (md.missions || []).filter(
        (m) => m?.source?.attempt_id === tid,
      );
      setMissions(myMissions);
    } catch (err) {
      console.warn('[StudentAssessmentResult] missions fetch failed:', err);
    }
  }, [tid]);

  useEffect(() => {
    (async () => {
      try {
        const meRes = await api.get('/auth/me');
        if (meRes.data?.child_id) {
          const cRes = await api.get(`/children/${meRes.data.child_id}`);
          setChild(cRes.data);
        }
      } catch (err) {
        console.warn('[StudentAssessmentResult] child fetch failed:', err);
      }
    })();
    load();
    loadMissions();
  }, [load, loadMissions]);

  // If results are released but reason-quality grading is still pending,
  // poll lightly so the badges appear without a manual refresh.
  useEffect(() => {
    if (!data?.released) return undefined;
    const answers = data?.attempt?.answers || [];
    const anyUngraded = answers.some((a) => a.reason_score == null);
    if (!anyUngraded) return undefined;
    const t = setTimeout(load, 6000);
    return () => clearTimeout(t);
  }, [data, load]);

  const brandColor = school?.brand_color || BRAND_COLOR_FALLBACK;
  const a = data?.assessment;
  const at = data?.attempt;
  const released = !!data?.released;
  const questions = data?.questions || [];

  const answersByQid = useMemo(() => {
    const map = {};
    for (const ans of at?.answers || []) map[ans.question_id] = ans;
    return map;
  }, [at]);

  const perQFeedbackByQid = useMemo(() => {
    const map = {};
    for (const item of at?.teacher_feedback_per_question || []) {
      map[item.question_id] = item.comment;
    }
    return map;
  }, [at]);

  if (loading || !at) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 pb-12"
      data-testid="student-assessment-result"
    >
      <header
        className="border-b bg-white/90 backdrop-blur sticky top-0 z-10"
        style={{ borderTopWidth: 4, borderTopColor: brandColor }}
      >
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/student/assessments')} data-testid="back-btn">
            <ArrowLeft size={16} className="mr-1.5" /> Back
          </Button>
          <div className="flex-1">
            <h1 className="font-bold text-slate-900">{a?.title}</h1>
            <p className="text-xs text-slate-500">{released ? 'Result' : 'Awaiting teacher review'}</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8 space-y-4">
        {!released ? (
          <div className="bg-white border rounded-xl p-8 text-center" data-testid="result-locked">
            <Hourglass size={32} className="mx-auto text-amber-500 mb-3" />
            <h2 className="text-lg font-semibold text-slate-900 mb-1">Awaiting teacher review</h2>
            <p className="text-sm text-slate-600 max-w-md mx-auto">
              You've submitted this assessment. Your teacher will share your result and feedback shortly.
              You'll receive an email when it's ready.
            </p>
            <p className="text-xs text-slate-400 mt-4">
              Submitted {at.submitted_at ? new Date(at.submitted_at).toLocaleString() : ''}
            </p>
          </div>
        ) : (
          <>
            <div className="bg-white border rounded-xl p-6 text-center" data-testid="result-score">
              <Trophy size={28} className="mx-auto mb-2" style={{ color: brandColor }} />
              <div className="text-4xl font-bold text-slate-900" data-testid="score-number">
                {at.total_score} <span className="text-slate-400 text-2xl">/ {at.max_score}</span>
              </div>
              <div className="text-sm text-slate-500 mt-1">{at.percent}%</div>
            </div>

            {at.teacher_overall_feedback && (
              <div
                className="bg-indigo-50 border border-indigo-200 rounded-xl p-4"
                data-testid="overall-feedback"
              >
                <div className="text-xs font-semibold uppercase text-indigo-600 mb-1">
                  Teacher's feedback
                </div>
                <p className="text-sm text-slate-800 whitespace-pre-wrap">{at.teacher_overall_feedback}</p>
              </div>
            )}

            {missions.length > 0 && (
              <div
                className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4"
                data-testid="missions-banner"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles size={16} className="text-amber-500" />
                  <span className="text-sm font-semibold text-slate-900">
                    {missions.length} topic{missions.length === 1 ? '' : 's'} to improve from this assessment
                  </span>
                </div>
                <p className="text-xs text-slate-600 mb-3">
                  Quick 5-minute catch-up missions tailored to where you can level up. Pick any one to start — no rush.
                </p>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                    {missions.slice(0, 3).map((m) => (
                      <Badge
                        key={m.mission_id}
                        variant="outline"
                        className="bg-white border-amber-200 text-amber-900 truncate max-w-[180px]"
                        title={m.concept_tag}
                        data-testid={`missions-banner-tag-${(m.concept_tag || '').replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        {m.concept_tag}
                      </Badge>
                    ))}
                    {missions.length > 3 && (
                      <span className="text-xs text-slate-500">+{missions.length - 3} more</span>
                    )}
                  </div>
                  <Button
                    size="sm"
                    onClick={() => navigate(`/student/missions${data?.attempt?.assessment_id ? `?assessment_id=${data.attempt.assessment_id}` : ''}`)}
                    data-testid="missions-banner-cta"
                  >
                    <Sparkles size={14} className="mr-1" /> Start catch-ups
                  </Button>
                </div>
              </div>
            )}

            {questions.map((q, idx) => {
              const ans = answersByQid[q.question_id];
              const correct = q.correct_option_id;
              const chosen = ans?.chosen_option_id;
              const isCorrect = chosen === correct;
              const teacherComment = perQFeedbackByQid[q.question_id];
              return (
                <div
                  key={q.question_id}
                  className={`bg-white border rounded-xl p-5 ${isCorrect ? 'border-emerald-200' : 'border-rose-200'}`}
                  data-testid={`result-q-${q.question_id}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-bold text-slate-700">Q{idx + 1}</span>
                    <Badge variant="outline" className={isCorrect ? 'bg-emerald-50 text-emerald-700 border-emerald-300' : 'bg-rose-50 text-rose-700 border-rose-300'}>
                      {isCorrect ? 'Correct' : 'Incorrect'}
                    </Badge>
                  </div>
                  <p className="text-slate-900 font-medium mb-3">{q.prompt}</p>

                  <div className="space-y-2 mb-3">
                    {q.options.map((opt) => {
                      const isCorrectOpt = opt.id === correct;
                      const isChosen = opt.id === chosen;
                      let cls = 'bg-slate-50 border border-slate-100';
                      if (isCorrectOpt) cls = 'bg-emerald-50 border border-emerald-200';
                      else if (isChosen) cls = 'bg-rose-50 border border-rose-200';
                      return (
                        <div key={opt.id} className={`flex items-start gap-2 px-3 py-2 rounded-lg text-sm ${cls}`}>
                          <span className="font-mono text-slate-500">{opt.id}.</span>
                          <span>{opt.text}</span>
                          {isCorrectOpt && <Badge variant="outline" className="ml-auto bg-emerald-100 text-emerald-700 border-emerald-300 text-[10px]">Correct</Badge>}
                          {!isCorrectOpt && isChosen && <Badge variant="outline" className="ml-auto bg-rose-100 text-rose-700 border-rose-300 text-[10px]">You</Badge>}
                        </div>
                      );
                    })}
                  </div>

                  <div className="bg-indigo-50/60 border border-indigo-100 rounded-lg p-3 text-sm text-slate-700 mb-3">
                    <strong className="text-indigo-700">Why it's correct:</strong> {q.correct_reasoning}
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-700 mb-3">
                    <strong className="text-slate-700">Your reason:</strong>{' '}
                    {ans?.student_reason || <span className="italic text-slate-400">No reason captured</span>}
                  </div>
                  {ans?.reason_score != null ? (
                    <div
                      className={`rounded-lg p-3 text-sm border mb-3 ${reasonScoreClasses(ans.reason_score)}`}
                      data-testid={`reason-score-${q.question_id}`}
                    >
                      <div className="flex items-center gap-1.5 font-semibold mb-1">
                        <Brain size={14} /> Reasoning quality: {ans.reason_score} / 5
                      </div>
                      {ans.reason_rationale && (
                        <p className="text-slate-700 text-xs leading-relaxed">{ans.reason_rationale}</p>
                      )}
                    </div>
                  ) : (
                    <div
                      className="rounded-lg p-3 text-xs border bg-slate-50 border-slate-200 text-slate-500 italic mb-3 flex items-center gap-1.5"
                      data-testid={`reason-grading-${q.question_id}`}
                    >
                      <Loader2 size={12} className="animate-spin" /> Reasoning grade is being prepared…
                    </div>
                  )}
                  {teacherComment && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-slate-700">
                      <strong className="text-amber-700">Teacher's note:</strong> {teacherComment}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </main>
    </div>
  );
}
